import React, { forwardRef, useImperativeHandle, useCallback } from 'react'
import Backdrop from '@mui/material/Backdrop'
import Box from '@mui/material/Box'
import MuiModal from '@mui/material/Modal'
import Fade from '@mui/material/Fade'
import Typography from '@mui/material/Typography'
import { Divider, styled } from '@mui/material'
import { useQueryState } from 'nuqs'
import { Close } from '@mui/icons-material'

const ModalContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  width: `calc(100% - ${theme.spacing(2)})`,
  maxWidth: theme.breakpoints.values.xl,
  zIndex: theme.zIndex.modal,
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
}))

/**
 * A reusable Modal component built with Material-UI that supports imperative handle control.
 *
 * @component
 * @param {Object} props - The props for the Modal component.
 * @param {React.ReactNode} props.children - The content to be displayed inside the modal.
 * @param {string} [props.title] - The title of the modal, displayed at the top.
 * @param {string} [props.description] - A description for the modal, displayed below the title.
 * @param {function} [props.onClose] - Callback function triggered when the modal is closed.
 * @param {React.Ref} ref - A ref object to control the modal programmatically.
 *
 * @returns {JSX.Element} The rendered Modal component.
 *
 * @example
 * const modalRef = useRef();
 *
 * const openModal = () => {
 *   modalRef.current.open();
 * };
 *
 * const closeModal = () => {
 *   modalRef.current.close();
 * };
 *
 * return (
 *   <>
 *     <button onClick={openModal}>Open Modal</button>
 *     <Modal
 *       ref={modalRef}
 *       title="Example Modal"
 *       description="This is an example modal."
 *       onClose={() => console.log('Modal closed')}
 *     >
 *       <p>Modal content goes here.</p>
 *     </Modal>
 *   </>
 * );
 */
const Modal = forwardRef(({ children, title, description, onClose }, ref) => {
  const [open, setOpen] = useQueryState('modalOpen', {
    defaultValue: false,
    type: 'boolean',
  })

  const handleOpen = useCallback(() => {
    if (!open) {
      // Prevent infinite re-renders by checking if already open
      setOpen(true)
    }
  }, [open, setOpen])

  const handleClose = useCallback(() => {
    if (open) {
      // Prevent infinite re-renders by checking if already closed
      setOpen(false)
      if (onClose) {
        requestAnimationFrame(() => onClose())
      }
    }
  }, [open, setOpen, onClose])

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }))

  return (
    <MuiModal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <Fade in={open}>
        <ModalContent>
          <Close
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              cursor: 'pointer',
              '&:hover': { color: 'olive' },
            }}
            onClick={handleClose}
          />
          {title && (
            <>
              <Typography
                id="transition-modal-title"
                variant="h4"
                component="h2"
              >
                {title}
              </Typography>
              <Divider sx={{ my: 2 }} />
            </>
          )}
          {description && (
            <Typography id="transition-modal-description" sx={{ mt: 2 }}>
              {description}
            </Typography>
          )}
          <Box sx={{ mt: 2 }}>{children}</Box>
        </ModalContent>
      </Fade>
    </MuiModal>
  )
})

export default Modal
